/**
 * PricingTiers — real, Stripe-shaped tier prices on the marketing site, built
 * from the shared pricing.json (the same source the start app uses — Rule 17,
 * single source of truth, no hardcoded prices). Currency + billing toggles are
 * visible; each tier CTA deep-links to start.inferrex.com's signup with the
 * tier pre-selected. Composes Card + Button + Badge + tokens — no bespoke CSS.
 */
import { useState } from 'react';
import { Check } from 'lucide-react';
import { Card } from '../card';
import { Button } from '../button';
import { Badge } from '../badge';
import { MarketingLink } from '../marketing-link';
import { Segmented } from './Segmented';
import { cn } from '../../utils';
import rawPricing from '../../data/pricing.json';
import { toPricingPageData, type RawPricingResponse } from '../../data/pricing-transform';
import type { PricingData, Currency, BillingFrequency } from '../../pricing';

const DATA: PricingData = toPricingPageData(rawPricing as RawPricingResponse);

const SYMBOL: Record<Currency, string> = { GBP: '£', EUR: '€', USD: '$' };
const CURRENCIES: readonly Currency[] = ['GBP', 'EUR', 'USD'];

function fmt(amount: number, currency: Currency): string {
  return `${SYMBOL[currency]}${Math.round(amount).toLocaleString('en-GB')}`;
}

export interface PricingTiersProps {
  /** Deep-link base for "Contact us" tiers (price === null). */
  readonly contactHref?: string;
}

export function PricingTiers({ contactHref = '/contact' }: PricingTiersProps) {
  const [currency, setCurrency] = useState<Currency>('GBP');
  const [frequency, setFrequency] = useState<BillingFrequency>('monthly');

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
        <Segmented
          value={currency}
          onChange={setCurrency}
          options={CURRENCIES.map((c) => ({ v: c, label: c }))}
        />
        <Segmented
          value={frequency}
          onChange={setFrequency}
          options={[{ v: 'monthly', label: 'Monthly' }, { v: 'annual', label: 'Annual −10%' }] as const}
        />
      </div>

      <div className="grid grid-cols-1 gap-(--space-grid) sm:grid-cols-2 lg:grid-cols-3">
        {DATA.tiers.map((tier) => {
          const total = tier.prices[currency][frequency];
          const perMonth = total === null ? null : frequency === 'annual' ? total / 12 : total;
          return (
            <Card
              key={tier.id}
              className={cn('flex h-full flex-col p-(--space-card)', tier.recommended && 'border-primary ring-1 ring-primary')}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-lg font-semibold text-foreground">{tier.name}</h3>
                {tier.recommended && <Badge>Popular</Badge>}
              </div>
              {tier.tag && <p className="mt-1 text-sm text-muted-foreground">{tier.tag}</p>}

              <div className="mt-4">
                {perMonth === null ? (
                  <div className="font-display text-3xl font-bold text-foreground">Custom</div>
                ) : (
                  <>
                    <span className="font-display text-3xl font-bold text-primary">{fmt(perMonth, currency)}</span>
                    <span className="text-sm text-muted-foreground"> /mo</span>
                    {frequency === 'annual' && total !== null && (
                      <p className="mt-1 text-xs text-muted-foreground">{fmt(total, currency)} billed annually</p>
                    )}
                  </>
                )}
              </div>

              <ul className="mt-4 space-y-1.5 text-sm">
                {(tier.quota ?? []).slice(0, 5).map((q, qi) => (
                  <li key={qi} className="flex items-baseline justify-between gap-2">
                    <span className="text-muted-foreground">{q.label}</span>
                    <span className="font-medium text-foreground">{q.value}</span>
                  </li>
                ))}
              </ul>

              <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                {tier.features.slice(0, 3).map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
                    <span>{f.label}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-5">
                <Button asChild className="w-full" variant={tier.recommended ? 'default' : 'outline'}>
                  <MarketingLink href={tier.ctaHref || contactHref}>{tier.ctaLabel}</MarketingLink>
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        Prices from Stripe · all features included on every tier · unlimited users · cancel within 30 days.
      </p>
    </div>
  );
}
